import { useEffect, useState } from "react";
import VariantEditor, { emptyVariants } from "../components/VariantEditor";
import MediaPicker from "../components/MediaPicker";
import RepostFields from "../components/RepostFields";
import CommentReminderField from "../components/CommentReminderField";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useFlash } from "../lib/flash";
import { krInput, toLocalInput, fromLocalInput, slotsOf } from "../lib/format";
import SlotEditor, { slotsToRows, rowsToSlots, rowsError, defaultRows } from "../components/SlotEditor";
import { useAuth, canSimulate } from "../lib/auth";

function defaults() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  const at = (h) => {
    const x = new Date(d);
    x.setHours(h, 0, 0, 0);
    return x.toISOString();
  };
  return {
    title: "",
    description: "",
    price: "",
    normal_price: "",
    stock_total: 20,
    max_per_customer: 3,
    slots: defaultRows(),
    sales_start: null,
    sales_end: at(12),
    waitlist_enabled: true,
    when: "now",
    publish_at: null,
    has_variants: false,
    variant_label: "Farve",
    variants: emptyVariants(),
    media: [],
    repost_enabled: false,
    repost_days: 2,
    repost_text: "",
    comment_reminder: true,
  };
}

function nextHour() {
  const d = new Date(Date.now() + 3600e3);
  d.setMinutes(0, 0, 0);
  return d.toISOString();
}

export default function OfferForm({ editId }) {
  const { store } = useAuth();
  const nav = useNavigate();
  const flash = useFlash();
  const [v, setV] = useState(defaults());
  const [editing, setEditing] = useState(!!editId);
  const [offer, setOffer] = useState(null);
  const [errors, setErrors] = useState([]);
  const [busy, setBusy] = useState(false);
  const [held, setHeld] = useState({});
  const [shopReminder, setShopReminder] = useState(null);

  useEffect(() => {
    if (store?.staff) return;
    api.get("/settings").then((r) => setShopReminder(r.data.settings?.comment_reminder === "1")).catch(() => {});
  }, [store?.staff]);

  useEffect(() => {
    if (!editId) return;
    api.get(`/offers/${editId}`).then((r) => {
      const o = r.data.offer;
      setOffer(o);
      setEditing(true);
      setV({
        title: o.title,
        description: o.description || "",
        price: krInput(o.price_ore),
        normal_price: krInput(o.normal_price_ore),
        stock_total: o.stock_total,
        max_per_customer: o.max_per_customer,
        slots: slotsToRows(slotsOf(o)),
        sales_start: o.sales_start,
        sales_end: o.sales_end,
        waitlist_enabled: !!o.waitlist_enabled,
        when: o.status === "scheduled" ? "scheduled" : "now",
        publish_at: o.publish_at,
        image_path: o.media?.length ? null : o.image_path,
        media: o.media || [],
        has_variants: !!(o.variants && o.variants.length),
        variant_label: o.variant_label || "Farve",
        variants: o.variants?.length ? o.variants.map((x) => ({ ...x, aliases: (x.aliases || []).join(", ") })) : emptyVariants(),
        repost_enabled: !!o.repost_enabled,
        repost_days: o.repost_days || 2,
        repost_text: o.repost_text || "",
        comment_reminder: !o.comment_reminder_off,
      });
      setHeld(Object.fromEntries((r.data.stats.variants || []).map((x) => [x.id, x.held])));
    });
  }, [editId]);

  const published = editing && offer && !!offer.fb_post_id;
  const canSchedule = !editing || (offer && ["draft", "scheduled"].includes(offer.status));

  const set = (k) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setV((s) => ({ ...s, [k]: val }));
  };
  const setDate = (k) => (e) => setV((s) => ({ ...s, [k]: fromLocalInput(e.target.value) }));

  const submit = async (action) => {
    const slotErr = rowsError(v.slots);
    if (slotErr) return setErrors([slotErr]);
    const pickupSlots = rowsToSlots(v.slots);
    setBusy(true);
    setErrors([]);
    const body = {
      title: v.title,
      description: v.description,
      price: v.price,
      normal_price: v.normal_price,
      stock_total: Number(v.stock_total),
      max_per_customer: Number(v.max_per_customer),
      pickup_slots: pickupSlots,
      pickup_start: pickupSlots[0].start,
      pickup_end: pickupSlots[pickupSlots.length - 1].end,
      sales_start: v.sales_start || null,
      sales_end: v.sales_end || null,
      waitlist_enabled: v.waitlist_enabled,
      when: v.when,
      publish_at: v.publish_at,
      action,
      image_data: null,
      media_ids: v.media.length || editing ? v.media.map((m) => m.id) : null,
      variant_label: v.has_variants ? v.variant_label : null,
      variants: v.has_variants ? v.variants.map((x) => ({ id: x.id, name: x.name, qty: Number(x.qty), aliases: x.aliases })) : editing && offer?.variants?.length ? [] : null,
      repost_enabled: v.repost_enabled,
      repost_days: Number(v.repost_days),
      repost_text: v.repost_text,
      comment_reminder: shopReminder ? v.comment_reminder : null,
    };
    if (v.has_variants) body.stock_total = v.variants.reduce((a, x) => a + (Number(x.qty) || 0), 0);
    try {
      const r = editing ? await api.put(`/offers/${editId}`, body) : await api.post("/offers", body);
      if (r.data.flash) flash(r.data.flash);
      nav(`/offers/${r.data.id}`);
    } catch (err) {
      if (err.response?.status === 422 && err.response.data?.errors) setErrors(err.response.data.errors);
      else flash(apiError(err.response?.data?.detail) || err.message, "error");
      window.scrollTo(0, 0);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{editing ? "Rediger tilbud" : "Nyt ja tak-tilbud"}</h1>
          <p>
            {published
              ? "Ændringer opdaterer også opslaget på Facebook."
              : 'Kunderne skriver "ja tak" under opslaget – robotten klarer resten.'}
          </p>
        </div>
      </div>
      {errors.length > 0 && (
        <div className="notice error" data-testid="offer-errors">
          {errors.map((e, i) => (
            <div key={i}>{e}</div>
          ))}
        </div>
      )}
      <form
        className="grid cols-main"
        onSubmit={(e) => {
          e.preventDefault();
          submit(canSchedule ? "publish" : "save");
        }}
      >
        <div>
          <section className="panel">
            <div className="field">
              <label htmlFor="title">Vare</label>
              <input id="title" required maxLength={120} value={v.title} onChange={set("title")} placeholder="Fx Hel flæskesteg, ca. 2 kg" data-testid="offer-title" />
            </div>
            <div className="field">
              <label htmlFor="description">Beskrivelse</label>
              <textarea id="description" maxLength={1500} value={v.description} onChange={set("description")} placeholder="Hvad gør tilbuddet godt?" data-testid="offer-description" />
            </div>
            <div className="row">
              <div className="field">
                <label htmlFor="price">Ja tak-pris (kr.)</label>
                <input id="price" inputMode="decimal" required value={v.price} onChange={set("price")} placeholder="49,95" data-testid="offer-price" />
              </div>
              <div className="field">
                <label htmlFor="normal_price">Normalpris (kr.)</label>
                <input id="normal_price" inputMode="decimal" value={v.normal_price} onChange={set("normal_price")} placeholder="89,95" data-testid="offer-normal-price" />
                <div className="hint">Vises som "du sparer". Besparelsen skal være reel.</div>
              </div>
            </div>
            <label className="check" style={{ margin: "4px 0 12px" }}>
              <input type="checkbox" checked={v.has_variants} onChange={set("has_variants")} data-testid="offer-has-variants" />
              Produktet har varianter (fx farver eller størrelser)
            </label>
            {v.has_variants && (
              <VariantEditor label={v.variant_label} variants={v.variants} locked={held}
                onLabel={(l) => setV((s) => ({ ...s, variant_label: l }))} onChange={(vs) => setV((s) => ({ ...s, variants: vs }))} />
            )}
            <div className="row">
              {!v.has_variants && (
                <div className="field">
                  <label htmlFor="stock_total">Antal på lager</label>
                  <input id="stock_total" type="number" min={1} max={100000} required value={v.stock_total} onChange={set("stock_total")} data-testid="offer-stock" />
                </div>
              )}
              <div className="field">
                <label htmlFor="max_per_customer">Max pr. kunde</label>
                <input id="max_per_customer" type="number" min={1} max={99} required value={v.max_per_customer} onChange={set("max_per_customer")} data-testid="offer-max" />
              </div>
            </div>
          </section>
          <section className="panel">
            <h2>Tider</h2>
            <h3 style={{ marginTop: 0 }}>Afhentning</h3>
            <SlotEditor rows={v.slots} onChange={(slots) => setV((s) => ({ ...s, slots }))} />
            <h3 style={{ marginTop: 6 }}>
              Salgsperiode <span className="muted" style={{ fontWeight: 400 }}>(valgfri)</span>
            </h3>
            <div className="row dates">
              <div className="field">
                <label htmlFor="sales_start">Bestil fra</label>
                <input id="sales_start" type="datetime-local" value={toLocalInput(v.sales_start)} onChange={setDate("sales_start")} data-testid="offer-sales-start" />
                <div className="hint">Tom = kunder kan bestille med det samme.</div>
              </div>
              <div className="field">
                <label htmlFor="sales_end">Bestil senest</label>
                <input id="sales_end" type="datetime-local" value={toLocalInput(v.sales_end)} onChange={setDate("sales_end")} data-testid="offer-sales-end" />
                <div className="hint">Tom = indtil afhentningen slutter.</div>
              </div>
            </div>
            <label className="check">
              <input type="checkbox" checked={v.waitlist_enabled} onChange={set("waitlist_enabled")} data-testid="offer-waitlist" /> Venteliste når der er
              udsolgt
            </label>
            <RepostFields v={v} setV={setV} set={set} />
            <CommentReminderField shopOn={shopReminder} v={v} set={set} />
          </section>
        </div>
        <aside>
          <section className="panel">
            <h2>Billeder eller video</h2>
            <MediaPicker media={v.media} onChange={(m) => setV((s) => ({ ...s, media: m }))} legacyImage={v.image_path} disabled={busy} />
          </section>
          <section className="panel">
            {canSchedule && (
              <>
                <h2>Hvornår skal opslaget på Facebook?</h2>
                <div className="when" role="radiogroup">
                  <label className="check">
                    <input type="radio" name="when" value="now" checked={v.when === "now"} onChange={set("when")} /> Med det
                    samme
                  </label>
                  <label className="check">
                    <input type="radio" name="when" value="scheduled" checked={v.when === "scheduled"} onChange={set("when")} /> Planlæg
                    tidspunkt
                  </label>
                </div>
                {v.when === "scheduled" && (
                  <div className="field">
                    <label htmlFor="publish_at">Slå op</label>
                    <input
                      id="publish_at"
                      type="datetime-local"
                      value={toLocalInput(v.publish_at || nextHour())}
                      onChange={setDate("publish_at")}
                      data-testid="offer-publish-at"
                    />
                  </div>
                )}
              </>
            )}
            <div className="actions" style={{ flexDirection: "column", alignItems: "stretch" }}>
              {canSchedule ? (
                <>
                  <button type="submit" className="btn jatak big" disabled={busy} data-testid="offer-publish-btn">
                    {v.when === "scheduled" ? "Planlæg opslag" : "Slå op på Facebook"}
                  </button>
                  <button type="button" className="btn" disabled={busy} onClick={() => submit("draft")} data-testid="offer-draft-btn">
                    Gem som kladde
                  </button>
                </>
              ) : (
                <button type="submit" className="btn primary big" disabled={busy} data-testid="offer-save-btn">
                  Gem ændringer
                </button>
              )}
            </div>
            <p className="muted" style={{ marginTop: 10, fontSize: 13 }}>
              {canSimulate(store)
                ? "Testtilstand: opslaget vises i Simulatoren i stedet for på Facebook."
                : "Testtilstand: opslaget gemmes kun i JaTak Pro og vises ikke på Facebook, før du forbinder din side."}
            </p>
          </section>
        </aside>
      </form>
    </>
  );
}
